import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ShortcodeItem } from '../../../interfaces/Presentation';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import {
  copySVG,
  search2SVG,
  crossSVG,
  filledCheckboxSVG,
} from '../../../lib/allCustomSVGs';
import PaginationMain from '../../../components/pagination/PaginationMain';
import Tabs from '../../../components/Tabs/Tabs';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import '../../../styles/pages/notifications/reportsPresentations/shortcodesPopup.scss';

type Props = {
  shortcodes: ShortcodeItem[];
  loading?: boolean;
  onClose: () => void;
};

type TabType = 'insight' | 'custom_prompt' | 'graph';

const ShortcodesPopup = ({ shortcodes, loading = false, onClose }: Props) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [currentTab, setCurrentTab] = useState(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const itemsPerPage = 5;

  const tabTypes: TabType[] = ['insight', 'custom_prompt', 'graph'];
  const tabItems = ['Insights', 'CustomPrompts', 'Graphs'];

  const tabFilteredShortcodes = useMemo(() => {
    const activeTabType = tabTypes[currentTab];
    return shortcodes.filter((shortcode) => shortcode.type === activeTabType);
  }, [shortcodes, currentTab]);

  const filteredShortcodes = useMemo(() => {
    let filtered = tabFilteredShortcodes;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = tabFilteredShortcodes.filter((shortcode) => {
        if (shortcode.type === 'insight') {
          return (
            shortcode.mcp_info?.name?.toLowerCase().includes(query) ||
            shortcode.metric?.toLowerCase().includes(query)
          );
        } else {
          return shortcode.title?.toLowerCase().includes(query);
        }
      });
    }

    return filtered;
  }, [tabFilteredShortcodes, searchQuery]);

  const pageCount = Math.ceil(filteredShortcodes.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedShortcodes = filteredShortcodes.slice(startIndex, endIndex);

  const handlePageClick = (event: { selected: number }) => {
    setCurrentPage(event.selected);
  };

  const handleTabChange = (index: number) => {
    setCurrentTab(index);
    setCurrentPage(0);
    setSearchQuery('');
  };

  const handleCopy = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(itemId);
      setTimeout(() => setCopiedId(null), 900);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getItemLabel = (shortcode: ShortcodeItem): string => {
    if (shortcode.type === 'insight') {
      return shortcode.metric || '-';
    } else {
      return shortcode.title || '-';
    }
  };

  return (
    <div className='shortcodesPopup'>
      <div className='shortcodesPopup__header'></div>

      {loading ? (
        <div className='shortcodesPopup__loading'>
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className='shortcodesPopup__tabs'>
            <Tabs
              currentTab={currentTab}
              setCurrentTab={handleTabChange}
              tabItems={tabItems}
            />
          </div>

          <div className='shortcodesPopup__search'>
            <div className='shortcodesPopup__search--icon'>
              <CustomSVGs svg={search2SVG} />
            </div>
            <input
              type='text'
              placeholder={
                currentTab === 0 ? t('SearchByIntegration') : t('SearchByName')
              }
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(0);
              }}
              className='shortcodesPopup__search--input'
            />
            {searchQuery && (
              <button
                type='button'
                className='shortcodesPopup__search--clear'
                onClick={() => {
                  setSearchQuery('');
                  setCurrentPage(0);
                }}
                aria-label='Clear search'
              >
                <CustomSVGs svg={crossSVG} />
              </button>
            )}
          </div>

          <div className='shortcodesPopup__table-container'>
            <table className='shortcodesPopup__table'>
              {currentTab === 0 ? (
                <colgroup className='shortcodesPopup__colgroup shortcodesPopup__colgroup--three-cols'>
                  <col className='shortcodesPopup__col shortcodesPopup__col--integration' />
                  <col className='shortcodesPopup__col shortcodesPopup__col--label' />
                  <col className='shortcodesPopup__col shortcodesPopup__col--shortcode' />
                </colgroup>
              ) : (
                <colgroup className='shortcodesPopup__colgroup shortcodesPopup__colgroup--two-cols'>
                  <col className='shortcodesPopup__col shortcodesPopup__col--label' />
                  <col className='shortcodesPopup__col shortcodesPopup__col--shortcode' />
                </colgroup>
              )}
              <thead>
                <tr>
                  {currentTab === 0 && <th>{t('Integration')}</th>}
                  <th>{currentTab === 0 ? t('Metric') : t('Name')}</th>
                  <th>{t('Shortcode')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedShortcodes.length > 0 ? (
                  paginatedShortcodes.map((shortcode, index) => {
                    const itemId = `${shortcode.short_code}-${index}`;
                    const isCopied = copiedId === itemId;
                    return (
                      <tr key={itemId}>
                        {currentTab === 0 && (
                          <td>
                            {shortcode.type === 'insight'
                              ? shortcode.mcp_info?.name || '-'
                              : '-'}
                          </td>
                        )}
                        <td>{getItemLabel(shortcode)}</td>
                        <td>
                          <div className='shortcodesPopup__shortcode-cell'>
                            <code className='shortcode-value'>
                              {shortcode.placeholder}
                            </code>
                            <button
                              type='button'
                              className='shortcodesPopup__copy-btn'
                              onClick={() =>
                                handleCopy(shortcode.placeholder, itemId)
                              }
                              title={isCopied ? t('Copied') : t('Copy')}
                              data-copied={isCopied.toString()}
                            >
                              <CustomSVGs
                                svg={isCopied ? filledCheckboxSVG : copySVG}
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={currentTab === 0 ? 3 : 2}
                      className='shortcodesPopup__no-data'
                    >
                      {t('NoShortcodesFound')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredShortcodes.length > 0 && (
            <div className='shortcodesPopup__pagination'>
              <PaginationMain
                key={`pagination-${currentTab}`}
                totalRecords={filteredShortcodes.length}
                itemsPerPage={itemsPerPage}
                onRecordsPerPageChange={() => {}}
                breakLabel='...'
                nextLabel={t('Next >')}
                onPageChange={handlePageClick}
                pageRangeDisplayed={2}
                initialPage={currentPage}
                pageCount={pageCount}
                previousLabel={t('< Previous')}
                marginPagesDisplayed={2}
                containerClassName='pagination'
                pageLinkClassName='pagination__page-num'
                previousLinkClassName='pagination__page-num'
                nextLinkClassName='pagination__page-num'
                disabledLinkClassName='pagination__page-num-disabled'
                activeLinkClassName='pagination__active'
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShortcodesPopup;
